import { useCallback, useState } from "react";
import { getAgentById as getBuiltinAgentById } from "@/data/aiToolkit";
import { runAgentOnContent } from "@/lib/communications/runAgentOnContent";
import {
  getAgentActionType,
  type AgentActionType,
} from "@/lib/agentActionRouter";
import { ensureClickUpConnected } from "@/lib/clickupAgentPermissions";
import { ensureTelegramReadPermission } from "@/lib/telegramAgentPermissions";
import { resolveClickUpProjectsForResults } from "@/lib/clickup/resolveClickUpProject";
import type { ResolvedClickUpMeeting } from "@/lib/clickup/resolveClickUpProject";
import { aiAgentsService } from "@/services/aiAgentsService";
import type { Agent } from "@/types/aiToolkit";
import type { AgentAnalysisResult, CommunicationBatchProcessing } from "@/types/communications";
import type { CommunicationAnalyzeContentType } from "@shared/tokenSavings";

export interface AnalyzeItemInput {
  id: string;
  label: string;
  subject: string;
  from: string;
  to?: string;
  date?: string;
  body: string;
  contentType?: CommunicationAnalyzeContentType;
}

export interface ClickUpActionContext {
  agent: Agent;
  teamId: string;
  meetings: ResolvedClickUpMeeting[];
}

async function resolveAgent(agentId: string): Promise<Agent | null> {
  return getBuiltinAgentById(agentId) ?? (await aiAgentsService.getAgentById(agentId));
}

export interface TelegramActionContext {
  agent: Agent;
  chatId: string;
  results: AgentAnalysisResult[];
  items: AnalyzeItemInput[];
}

export function useCommunicationAnalyze() {
  const [results, setResults] = useState<AgentAnalysisResult[]>([]);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState<CommunicationBatchProcessing | null>(null);
  const [lastAgent, setLastAgent] = useState<Agent | null>(null);
  const [lastActionType, setLastActionType] = useState<AgentActionType>("none");
  const [clickUpActionOpen, setClickUpActionOpen] = useState(false);
  const [clickUpActionContext, setClickUpActionContext] = useState<ClickUpActionContext | null>(
    null
  );
  const [telegramActionOpen, setTelegramActionOpen] = useState(false);
  const [telegramActionContext, setTelegramActionContext] = useState<TelegramActionContext | null>(
    null
  );
  const [clickUpResolving, setClickUpResolving] = useState(false);
  const [lastItems, setLastItems] = useState<AnalyzeItemInput[]>([]);

  const restoreAnalyzeState = useCallback(
    (state: {
      results: AgentAnalysisResult[];
      agent: Agent | null;
      actionType: AgentActionType;
      clickUpContext?: ClickUpActionContext | null;
      items?: AnalyzeItemInput[];
    }) => {
      setResults(state.results);
      setLastAgent(state.agent);
      setLastActionType(state.actionType);
      if (state.items) setLastItems(state.items);

      if (state.actionType === "clickup" && state.clickUpContext) {
        setClickUpActionContext(state.clickUpContext);
        setClickUpActionOpen(true);
        setResultsOpen(false);
      } else if (state.actionType === "telegram_send" && state.telegramContext) {
        setTelegramActionContext(state.telegramContext);
        setTelegramActionOpen(true);
        setResultsOpen(false);
      } else {
        setClickUpActionContext(null);
        setClickUpActionOpen(false);
        setTelegramActionContext(null);
        setTelegramActionOpen(false);
        setResultsOpen(true);
      }
    },
    []
  );

  const runBatchAnalyze = useCallback(async (agentId: string, items: AnalyzeItemInput[]) => {
    if (!agentId || items.length === 0) return;

    if (items[0]?.contentType === "telegram") {
      try {
        await ensureTelegramReadPermission();
      } catch (err) {
        setResults([
          {
            itemId: items[0].id,
            itemLabel: items[0].label,
            content: "",
            error: err instanceof Error ? err.message : "Permission denied",
          },
        ]);
        setResultsOpen(true);
        return { actionType: "none" as const };
      }
    }

    const agent = await resolveAgent(agentId);
    setLastAgent(agent);
    setLastItems(items);

    setBatchProcessing({
      active: true,
      total: items.length,
      completed: 0,
    });

    const newResults: AgentAnalysisResult[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      setBatchProcessing({
        active: true,
        total: items.length,
        completed: i,
        currentItemId: item.id,
        currentLabel: item.label,
      });

      try {
        const result = await runAgentOnContent({
          agentId,
          subject: item.subject,
          from: item.from,
          to: item.to,
          date: item.date,
          body: item.body,
          contentType: item.contentType,
        });
        newResults.push({
          itemId: item.id,
          itemLabel: item.label,
          content: result.content,
          parsed: result.parsed,
          confidence: result.confidence,
        });
      } catch (err) {
        newResults.push({
          itemId: item.id,
          itemLabel: item.label,
          content: "",
          error: err instanceof Error ? err.message : "Analysis failed",
        });
      }
    }

    setBatchProcessing(null);
    setResults((prev) => {
      const byId = new Map(prev.map((r) => [r.itemId, r]));
      for (const r of newResults) byId.set(r.itemId, r);
      return Array.from(byId.values());
    });

    const actionType = getAgentActionType(agent, newResults);
    setLastActionType(actionType);

    if (actionType === "telegram_send" && agent) {
      const chatId = String(items[0]?.to || "");
      setTelegramActionContext({
        agent,
        chatId,
        results: newResults,
        items,
      });
      setTelegramActionOpen(true);
      return { actionType: "telegram_send" as const };
    }

    if (actionType === "clickup") {
      const connected = await ensureClickUpConnected();
      if (!connected) {
        setResultsOpen(true);
        return { actionType: "none" as const, error: "ClickUp is not connected" };
      }

      setClickUpResolving(true);
      try {
        const resolved = await resolveClickUpProjectsForResults(newResults);
        if (agent) {
          setClickUpActionContext({
            agent,
            teamId: resolved.teamId,
            meetings: resolved.meetings,
          });
          setClickUpActionOpen(true);
        } else {
          setResultsOpen(true);
        }
      } catch {
        setResultsOpen(true);
      } finally {
        setClickUpResolving(false);
      }
      return { actionType: "clickup" as const };
    }

    setResultsOpen(true);
    return { actionType: "none" as const };
  }, []);

  return {
    results,
    resultsOpen,
    setResultsOpen,
    batchProcessing,
    runBatchAnalyze,
    isAnalyzing: batchProcessing?.active ?? false,
    clickUpResolving,
    lastAgent,
    lastActionType,
    clickUpActionOpen,
    setClickUpActionOpen,
    clickUpActionContext,
    telegramActionOpen,
    setTelegramActionOpen,
    telegramActionContext,
    lastItems,
    restoreAnalyzeState,
  };
}
